import { useEffect, useState } from 'react'
import { COMMON_ZONES, shipNowOr, setShipTz } from '../lib/shipTime'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import Modal from '../components/Modal'
import AIActivity from './AIActivity'
import PartnerKeys from '../components/PartnerKeys'
import ConnectorsAdmin from '../components/ConnectorsAdmin'
import EmailSettings from '../components/EmailSettings'
import BrainMapAdmin from '../components/BrainMapAdmin'

import { ALL_MODULES, PLAN_FEATURES, effectiveFeatures, planLabel } from '../lib/features'
import { PERM_MODULES } from '../lib/permModules'

// Role presets for crew permissions (matched on the module key). Order: none -> write -> read (default).
const PERM_PRESETS = {
  'Captain / CO': { write: /./ },
  'Engineer': { none: /^(crew|budget)$/, write: /maint|equip|invent|logbook|todo|checklist|refit|bunker/ },
  'Deck / Bosun': { none: /^(crew|budget)$/, write: /todo|checklist|lsa|logbook|diary|drill/ },
  'Interior / Stew': { none: /^(crew|budget|maintenance|equipment|refit)$/, write: /todo|checklist|invent/ },
  'Tester (Ball)': { write: /./ },
}
function applyPreset(name) {
  const r = PERM_PRESETS[name] || {}
  return Object.fromEntries(PERM_MODULES.map(([k]) => [k, r.none && r.none.test(k) ? 'none' : r.write && r.write.test(k) ? 'write' : 'read']))
}


function TimezoneCard({ clients, config, onSaved }) {
  const [vesselId, setVesselId] = useState('')
  const [tz, setTz] = useState('')
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState('')
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000)
    return () => clearInterval(id)
  }, [])

  // Default to the vessel currently logged in, else the first in the list.
  useEffect(() => {
    if (vesselId || !clients?.length) return
    const mine = clients.find(c => c.id === config?.id)
    setVesselId((mine || clients[0]).id)
  }, [clients, config?.id]) // eslint-disable-line

  const vessel = (clients || []).find(c => c.id === vesselId) || null
  useEffect(() => { setTz(vessel?.timezone || ''); setMsg('') }, [vesselId]) // eslint-disable-line

  const preview = shipNowOr(tz || null)

  async function save() {
    if (!vesselId) return
    setSaving(true); setMsg('')
    try {
      const { error } = await supabase.from('clients').update({ timezone: tz || null }).eq('id', vesselId)
      if (error) throw new Error(error.message)
      // Only re-register the live zone if we just changed the vessel we're logged into.
      if (vesselId === config?.id) setShipTz(tz || null)
      setMsg("Ship's time for " + (vessel?.vessel_name || 'vessel') + ' set to ' + (tz || 'device local') + '. Reload to apply.')
      if (onSaved) onSaved()
    } catch (e) { setMsg('Failed: ' + (e.message || 'unknown')) }
    finally { setSaving(false) }
  }

  return (
    <div className="card" style={{ padding: 20, maxWidth: 560 }}>
      <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>Ship's Time</h3>
      <p style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.6, marginBottom: 16 }}>
        MLC records must be kept in the vessel's time, set by the Master &mdash; not the timezone of
        whatever device happens to be open. Hours of Rest, Watch Schedule, Duty Rota and drill records
        all take their day boundary from this setting. Change it when the ship's clocks change.
      </p>

      <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Vessel</label>
      <select
        value={vesselId}
        onChange={e => setVesselId(e.target.value)}
        style={{ width: '100%', marginTop: 6, marginBottom: 14, padding: '9px 11px', borderRadius: 8, border: '1px solid var(--line)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, appearance: 'none' }}
      >
        {(clients || []).map(c => (
          <option key={c.id} value={c.id}>
            {c.vessel_name}{c.timezone ? ' \u00b7 ' + c.timezone : ' \u00b7 not set'}
          </option>
        ))}
      </select>

      <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Vessel Timezone</label>
      <select
        value={tz}
        onChange={e => setTz(e.target.value)}
        style={{ width: '100%', marginTop: 6, marginBottom: 14, padding: '9px 11px', borderRadius: 8, border: '1px solid var(--line)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, appearance: 'none' }}
      >
        <option value="">Not set (use device local time)</option>
        {COMMON_ZONES.map(z => <option key={z} value={z}>{z}</option>)}
      </select>

      <div style={{ border: '1px solid var(--line)', borderRadius: 8, padding: '12px 14px', marginBottom: 14, background: 'var(--bg2)' }}>
        <div style={{ fontSize: 10, letterSpacing: '.1em', color: 'var(--muted)', textTransform: 'uppercase', marginBottom: 4 }}>
          {tz ? "Ship's time now" : 'Device local time now'}
        </div>
        <div style={{ fontSize: 20, fontWeight: 600, color: 'var(--gold2)', fontVariantNumeric: 'tabular-nums' }}>
          {preview.hm}
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>{preview.date}</div>
      </div>

      <button className="btn btn-primary" disabled={saving} onClick={save}>
        {saving ? 'Saving...' : "Save Ship's Time"}
      </button>
      {msg && <div style={{ marginTop: 12, fontSize: 12, color: msg.startsWith('Failed') ? '#b4313c' : 'var(--muted)' }}>{msg}</div>}
    </div>
  )
}

const MODULE_LABELS = {
  dashboard:'Dashboard', chat:'AI Assistant', announcements:'Crew Board', equipment:'Equipment',
  mlog:'Maintenance Log', mtask:'Maintenance Tasks', inventory:'Inventory', docs:'Documents',
  checklists:'Checklists', todo:'To Do', lsa:'LSA / FFE', risk_assessments:'Risk Assessments',
  refit:'Refit / Yard Period', budget:'Budget', crew:'Crew', calendar:'Calendar', charters:'Charters', apa:'APA',
  port_calls:'Port Calls', marina_bookings:'Marina Bookings', hours_rest:'Hours of Rest',
  watch_schedule:'Watch Schedule', officer_review:'Monthly Review', manuals:'Manuals',
  duty_rota:'Duty Rota', logbooks:'Log Books', weather:'Weather', regulations:'Regulations',
  passage_plan:'Passage Plans', workspaces:'Workspaces',
}

function ManageVessel({ vessel, onClose, onSaved }) {
  const [plan, setPlan] = useState(vessel.plan || 'starter')
  const [status, setStatus] = useState(vessel.sub_status || 'active')
  const [trialEnds, setTrialEnds] = useState(vessel.trial_ends || '')
  const [renewsOn, setRenewsOn] = useState(vessel.renews_on || '')
  const [overrides, setOverrides] = useState(vessel.features || {})
  // monthly AI allowance in USD (sql/ai-budget.sql); '' = no limit. No column yet (SQL not run) -> not sent
  const hasLimit = 'ai_monthly_limit_usd' in vessel
  const [aiLimit, setAiLimit] = useState(vessel.ai_monthly_limit_usd == null ? '' : String(Number(vessel.ai_monthly_limit_usd)))
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')

  const planDefault = new Set(PLAN_FEATURES[plan] || PLAN_FEATURES.starter)
  // effective = plan default with overrides applied
  const effective = new Set(planDefault)
  for (const [m,on] of Object.entries(overrides)) { if(on) effective.add(m); else effective.delete(m) }

  function toggle(mod) {
    const inDefault = planDefault.has(mod)
    const cur = effective.has(mod)
    const next = !cur
    setOverrides(o => {
      const c = { ...o }
      if (next === inDefault) delete c[mod]   // back to plan default -> no override
      else c[mod] = next                       // explicit override
      return c
    })
  }

  async function save() {
    setSaving(true); setErr('')
    try {
      if (hasLimit && aiLimit.trim() !== '' && !(Number(aiLimit) >= 0)) throw new Error('AI limit: a number of USD, or empty for no limit')
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/api/admin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + (session?.access_token || '') },
        body: JSON.stringify({
          action: 'update_client', client_id: vessel.id,
          plan, sub_status: status, trial_ends: trialEnds || null, renews_on: renewsOn || null, features: overrides,
          ...(hasLimit ? { ai_monthly_limit_usd: aiLimit.trim() === '' ? null : Number(aiLimit) } : {}),
        }),
      })
      const json = await res.json().catch(()=>({}))
      if (!res.ok) throw new Error(json.error || ('HTTP '+res.status))
      onSaved(); onClose()
    } catch (e) { setErr(e.message) }
    finally { setSaving(false) }
  }

  const box = { position:'fixed', inset:0, background:'rgba(20,30,50,.5)', display:'flex', alignItems:'flex-start', justifyContent:'center', padding:'32px 16px', zIndex:1000, overflow:'auto' }
  const card = { background:'#fff', borderRadius:14, padding:24, width:640, maxWidth:'100%' }
  const lab = { fontSize:10, fontWeight:700, letterSpacing:'.06em', color:'#5b6878', textTransform:'uppercase' }
  const inp = { width:'100%', marginTop:3, padding:'8px 10px', borderRadius:8, border:'1px solid #e4e9f0', fontSize:13 }

  return (
    <div style={box} onClick={onClose}>
      <div style={card} onClick={e=>e.stopPropagation()}>
        <div style={{ fontSize:18, fontWeight:700, color:'#15294a', marginBottom:2 }}>Manage {vessel.vessel_name}</div>
        <div style={{ fontSize:12, color:'#5b6878', marginBottom:16 }}>{vessel.email}</div>

        <div style={{ display:'flex', gap:12, marginBottom:14 }}>
          <label style={{ flex:1 }}><span style={lab}>Plan</span>
            <select value={plan} onChange={e=>{ setPlan(e.target.value); setOverrides({}) }} style={inp}>
              <option value="starter">Starter</option><option value="professional">Professional</option><option value="enterprise">Enterprise</option>
            </select></label>
          <label style={{ flex:1 }}><span style={lab}>Status</span>
            <select value={status} onChange={e=>setStatus(e.target.value)} style={inp}>
              <option value="active">Active</option><option value="trial">Trial</option><option value="suspended">Suspended</option><option value="cancelled">Cancelled</option>
            </select></label>
        </div>
        <div style={{ display:'flex', gap:12, marginBottom:18 }}>
          <label style={{ flex:1 }}><span style={lab}>Trial ends</span><input type="date" value={trialEnds||''} onChange={e=>setTrialEnds(e.target.value)} style={inp} /></label>
          <label style={{ flex:1 }}><span style={lab}>Renews on</span><input type="date" value={renewsOn||''} onChange={e=>setRenewsOn(e.target.value)} style={inp} /></label>
        </div>
        <div style={{ display:'flex', gap:12, marginBottom:18, alignItems:'flex-end' }}>
          <label style={{ flex:1 }}><span style={lab}>Monthly AI limit (USD)</span>
            <input type="number" min="0" step="1" inputMode="decimal" value={aiLimit} disabled={!hasLimit} onChange={e=>setAiLimit(e.target.value)} placeholder="No limit" style={inp} /></label>
          <div style={{ flex:1, fontSize:11.5, color:'#5b6878', paddingBottom:8 }}>
            {hasLimit ? 'Empty = no limit. When this month\u2019s AI cost reaches it, AI stops for the vessel until the 1st (the pages keep working). Email to you at 80%.' + (vessel.is_demo ? ' Demo copy: $10 by default.' : '') : 'Run sql/ai-budget.sql first.'}
          </div>
        </div>

        <div style={{ ...lab, marginBottom:6 }}>Features <span style={{ textTransform:'none', fontWeight:400, color:'#5b6878' }}>&mdash; {plan} plan defaults shown; toggle to override per vessel</span></div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'4px 16px', maxHeight:280, overflow:'auto', border:'1px solid #eef1f4', borderRadius:8, padding:10 }}>
          {ALL_MODULES.map(mod=>{
            const on = effective.has(mod)
            const overridden = overrides[mod] !== undefined
            return (
              <div key={mod} onClick={()=>toggle(mod)} style={{ display:'flex', alignItems:'center', gap:8, padding:'5px 6px', borderRadius:6, cursor:'pointer', background: overridden?'#fff7e6':'transparent' }}>
                <div style={{ width:34, height:19, borderRadius:10, background: on?'#1e7d43':'#cbd5e0', position:'relative', transition:'background .15s', flexShrink:0 }}>
                  <div style={{ position:'absolute', top:2, left: on?17:2, width:15, height:15, borderRadius:8, background:'#fff', transition:'left .15s' }} />
                </div>
                <span style={{ fontSize:12.5, color: on?'#26313c':'#9aa5b1' }}>{MODULE_LABELS[mod]||mod}</span>
                {overridden && <span style={{ fontSize:9, color:'#76601f', fontWeight:700 }}>OVERRIDE</span>}
              </div>
            )
          })}
        </div>

        {err && <div style={{ color:'#8a1f1f', fontSize:12, marginTop:10 }}>{err}</div>}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:16 }}>
          <button onClick={()=>setOverrides({})} style={{ background:'none', border:0, color:'#5b6878', fontSize:12, cursor:'pointer' }}>Reset to plan defaults</button>
          <div style={{ display:'flex', gap:10 }}>
            <button onClick={onClose} style={{ background:'#fff', border:'1px solid #e4e9f0', borderRadius:8, padding:'9px 16px', fontSize:13, cursor:'pointer' }}>Cancel</button>
            <button onClick={save} disabled={saving} style={{ background:'#15294a', color:'#fff', border:0, borderRadius:8, padding:'9px 20px', fontSize:13, fontWeight:600, cursor:'pointer' }}>{saving?'Saving\u2026':'Save'}</button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function Admin() {
  async function adminFetch(method, body) {
    const { data: { session } } = await supabase.auth.getSession()
    const res = await fetch('/api/admin', {
      method,
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + (session?.access_token || '') },
      body: body ? JSON.stringify(body) : undefined,
    })
    const json = await res.json().catch(()=>({}))
    if (!res.ok) throw new Error(json.error || ('HTTP '+res.status))
    return json
  }
  // admin access is granted by being logged in as an is_admin account (see below)
  const [pw, setPw] = useState('')
  const [clients, setClients] = useState([])
  const [companies, setCompanies] = useState([])
  const [companyForm, setCompanyForm] = useState(null) // {name,email,password} when creating
  async function loadCompanies() {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/api/admin?resource=companies', { headers: { Authorization: 'Bearer ' + (session?.access_token || '') } })
      const json = await res.json()
      setCompanies(json.companies || [])
    } catch (e) { console.error('loadCompanies failed', e) }
  }
  async function createCompany() {
    if (!companyForm?.name || !companyForm?.email || !companyForm?.password) { alert('Name, email and password required'); return }
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/api/workspace', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token }, body: JSON.stringify({ action: 'create_company', name: companyForm.name, email: companyForm.email, password: companyForm.password }) })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to create company')
      setCompanyForm(null); loadCompanies()
    } catch (e) { alert('Could not create company: ' + e.message) }
  }
  async function assignVessel(vesselId, companyId) {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/api/admin', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + (session?.access_token || '') }, body: JSON.stringify({ action: 'assign_vessel_company', vessel_id: vesselId, company_id: companyId || null }) })
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Failed') }
      loadClients()
    } catch (e) { alert('Could not assign vessel: ' + e.message) }
  }
  const [loading, setLoading] = useState(false)
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState({})
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const { config, isCrew, signIn } = useAuth()
  const authed = config?.is_admin === true
  const [loginEmail, setLoginEmail] = useState('')
  const [loginPw, setLoginPw] = useState('')
  const [loginErr, setLoginErr] = useState('')
  const [loggingIn, setLoggingIn] = useState(false)
  async function adminLogin(e) {
    e.preventDefault()
    setLoginErr(''); setLoggingIn(true)
    try {
      await signIn(loginEmail.trim(), loginPw)
      // config refreshes via auth listener; if the account isn't admin, the guard below shows the message.
    } catch (err) { setLoginErr('Invalid login credentials') }
    finally { setLoggingIn(false) }
  }
  const [tab, setTab] = useState('vessels')
  const [managing, setManaging] = useState(null)
  const [aiVessel, setAiVessel] = useState(null)
  const [permCrew, setPermCrew] = useState(null)
  const [permForm, setPermForm] = useState({})
  const [analytics, setAnalytics] = useState(null)
  const [feedbackRows, setFeedbackRows] = useState([])
  const [aiQuestions, setAiQuestions] = useState([])
  async function loadInsights() {
    try {
      const fb = await supabase.from('feedback').select('*').order('created_at', { ascending: false }).limit(100)
      setFeedbackRows(fb.data || [])
      const aq = await supabase.from('activity_events').select('actor_name, module, created_at').eq('action', 'ai_question').order('created_at', { ascending: false }).limit(100)
      setAiQuestions(aq.data || [])
    } catch (e) { console.error('insights load failed', e) }
  }
  async function resolveFeedback(id) {
    await supabase.from('feedback').update({ status: 'resolved' }).eq('id', id)
    setFeedbackRows(rows => rows.map(r => r.id === id ? { ...r, status: 'resolved' } : r))
  }
  const [crewList, setCrewList] = useState([])
  const [crewModal, setCrewModal] = useState(false)
  const [crewForm, setCrewForm] = useState({})
  const [crewSaving, setCrewSaving] = useState(false)
  const [crewError, setCrewError] = useState('')


  useEffect(() => { if (authed) loadClients() }, [authed])

  async function loadClients() {
    setLoading(true)
    try {
      const { clients } = await adminFetch('GET')
      setClients(clients || [])
    } catch (e) {
      console.error('loadClients failed', e)
      alert('Could not load clients: ' + e.message)
    }
    setLoading(false)
  }

  async function loadCrew() {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/api/admin?resource=crew', {
        headers: { Authorization: 'Bearer ' + (session?.access_token || '') },
      })
      const json = await res.json()
      setCrewList(json.crew || [])
    } catch (e) { console.error('loadCrew failed', e) }
  }

  useEffect(() => { if (authed) loadCrew() }, [authed])
  useEffect(() => { if (authed && tab==='analytics') { loadAnalytics(); loadInsights() } }, [authed, tab])
  useEffect(() => { if (authed) loadCompanies() }, [authed, tab]) // eslint-disable-line

  async function handleCrewSave() {
    setCrewSaving(true); setCrewError('')
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/api/workspace', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token },
        body: JSON.stringify({ action: 'create_crew', email: crewForm.email, password: crewForm.password, name: crewForm.name, role: crewForm.role || 'crew', client_id: crewForm.client_id || config?.id })
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to create crew account')
      setCrewModal(false); setCrewForm({}); loadCrew()
    } catch(e) { setCrewError(e.message) }
    finally { setCrewSaving(false) }
  }

  async function deactivateCrew(id) {
    if (!window.confirm('Deactivate this crew account? They will no longer be able to log in.')) return
    const { data: { session } } = await supabase.auth.getSession()
    await fetch('/api/workspace', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + session.access_token }, body: JSON.stringify({ action: 'deactivate_crew', crew_id: id }) })
    loadCrew()
  }

  async function handleSave() {
    setSaving(true); setSaveError('')
    try {
      await adminFetch('POST', {
        action: 'create_client',
        email: form.email,
        password: form.password,
        vessel_name: form.vessel_name,
        plan: form.plan || 'starter',
      })
      setModal(false)
      loadClients()
    } catch(e) {
      setSaveError(e.message)
    } finally { setSaving(false) }
  }

  async function loadAnalytics() {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/api/admin?resource=analytics', { headers: { Authorization: 'Bearer ' + (session?.access_token || '') } })
      const json = await res.json()
      setAnalytics(json)
    } catch (e) { console.error('analytics load failed', e) }
  }

  async function saveCrewPerms() {
    try {
      await adminFetch('POST', { action: 'update_crew_permissions', crew_id: permCrew.id, permissions: permForm })
      setPermCrew(null); loadCrew()
    } catch (e) { alert('Save failed: ' + e.message) }
  }

  async function deleteClient(client) {
    if (!window.confirm(`Remove client "${client.vessel_name}"? This will delete their login.`)) return
    try {
      await adminFetch('DELETE', { action: 'delete_client', client_id: client.id, user_id: client.user_id })
      loadClients()
    } catch (e) { alert('Remove failed: ' + e.message) }
  }

  if (!authed) return (
    <div style={styles.loginPage}>
      <div style={styles.loginBox}>
        <div style={{fontFamily:'Cormorant Garamond, serif',fontSize:22,color:'var(--gold2)',marginBottom:20}}>Hey Captain Admin</div>
        {config && !config.is_admin ? (
          <div style={{fontSize:13,color:'#c0434a',lineHeight:1.5}}>This account is not an administrator. Sign out and log in with an admin account.</div>
        ) : (
          <form onSubmit={adminLogin}>
            <div className="field">
              <label>Admin Email</label>
              <input type="email" value={loginEmail} onChange={e=>setLoginEmail(e.target.value)} placeholder="you@heycaptain.io" autoFocus autoComplete="username" />
            </div>
            <div className="field">
              <label>Password</label>
              <input type="password" value={loginPw} onChange={e=>setLoginPw(e.target.value)} placeholder="Your password" autoComplete="current-password" />
            </div>
            {loginErr && <div style={{fontSize:12,color:'#c0434a',margin:'6px 0'}}>{loginErr}</div>}
            <button type="submit" disabled={loggingIn} className="btn btn-primary" style={{width:'100%',padding:'10px',marginTop:6}}>{loggingIn ? 'Signing in…' : 'Sign in to Admin'}</button>
          </form>
        )}
      </div>
    </div>
  )

  return (
    <div style={styles.page}>
      <div style={styles.topBar}>
        <div>
          <div style={{fontFamily:'Cormorant Garamond, serif',fontSize:20,color:'var(--gold2)'}}>Hey Captain</div>
          <div style={{fontSize:10,color:'var(--muted)',letterSpacing:'.08em',textTransform:'uppercase'}}>Admin Panel</div>
        </div>
        <button className="btn btn-primary" onClick={() => { setForm({}); setSaveError(''); setModal(true) }}>
          + Add new client
        </button>
      </div>

      <div style={tab==='brain' ? { ...styles.content, maxWidth: 'none', paddingBottom: 0 } : styles.content}>
        <div style={{display:'flex',gap:8,marginBottom:20}}>
          <button className={'btn'+(tab==='vessels'?' btn-primary':'')} onClick={()=>setTab('vessels')}>Vessels ({clients.length})</button>
          <button className={'btn'+(tab==='crew'?' btn-primary':'')} onClick={()=>setTab('crew')}>Crew Accounts ({crewList.length})</button>
          <button className={'btn'+(tab==='companies'?' btn-primary':'')} onClick={()=>setTab('companies')}>Companies ({companies.length})</button>
          <button className={'btn'+(tab==='settings'?' btn-primary':'')} onClick={()=>setTab('settings')}>Vessel Settings</button>
          <button className={'btn'+(tab==='analytics'?' btn-primary':'')} onClick={()=>setTab('analytics')}>Analytics</button>
          <button className={'btn'+(tab==='ai'?' btn-primary':'')} onClick={()=>setTab('ai')}>AI Activity</button>
          <button className={'btn'+(tab==='partners'?' btn-primary':'')} onClick={()=>setTab('partners')}>Partner API</button>
          <button className={'btn'+(tab==='connectors'?' btn-primary':'')} onClick={()=>setTab('connectors')}>Connectors</button>
          <button className={'btn'+(tab==='email'?' btn-primary':'')} onClick={()=>setTab('email')}>Email</button>
          <button className={'btn'+(tab==='brain'?' btn-primary':'')} onClick={()=>setTab('brain')}>Brain map</button>
        </div>

        {tab==='analytics' && (
          <div>
            {!analytics ? <div className="no-rows">Loading analytics…</div> : (
              <div>
                <div style={{display:'flex',gap:12,marginBottom:20,flexWrap:'wrap'}}>
                  {[['Vessels', analytics.vessels?.length||0],['Events (30d)', analytics.total_events||0],['Active vessels (30d)', (analytics.vessels||[]).filter(v=>v.logins_30d>0).length]].map(([label,val])=>(
                    <div key={label} style={{background:'#fff',borderRadius:12,padding:'16px 20px',minWidth:150,border:'1px solid #eef1f4'}}>
                      <div style={{fontSize:26,fontWeight:700,color:'#15294a'}}>{val}</div>
                      <div style={{fontSize:11,color:'#5b6878',textTransform:'uppercase',letterSpacing:'.05em'}}>{label}</div>
                    </div>
                  ))}
                </div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))',gap:14}}>
                  {(analytics.vessels||[]).map(v=>(
                    <div key={v.id} style={{background:'#fff',borderRadius:12,padding:18,border:'1px solid #eef1f4'}}>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
                        <div style={{fontWeight:700,color:'#15294a',fontSize:15}}>{v.vessel_name}</div>
                        <span className="pill pill-b">{v.plan}</span>
                      </div>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px 12px',fontSize:12.5,marginBottom:12}}>
                        <div><span style={{color:'#5b6878'}}>Last active: </span>{v.last_activity?new Date(v.last_activity).toLocaleDateString(undefined,{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'}):'\u2014'}</div>
                        <div><span style={{color:'#5b6878'}}>Logins (30d): </span>{v.logins_30d}</div>
                        <div><span style={{color:'#5b6878'}}>Page views: </span>{v.page_views_30d}</div>
                        <div><span style={{color:'#5b6878'}}>Crew: </span>{v.crew_active}/{v.crew_total}</div>
                      </div>
                      {v.top_modules?.length>0 && (
                        <div>
                          <div style={{fontSize:10,color:'#5b6878',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:6}}>Most used</div>
                          {v.top_modules.map(([mod,count])=>{
                            const max = v.top_modules[0][1]||1
                            return (
                              <div key={mod} style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                                <div style={{width:90,fontSize:11.5,color:'#26313c',textTransform:'capitalize'}}>{mod.replace(/_/g,' ')}</div>
                                <div style={{flex:1,height:8,background:'#eef1f4',borderRadius:4,overflow:'hidden'}}><div style={{width:(count/max*100)+'%',height:'100%',background:'#2457d6'}}/></div>
                                <div style={{width:24,fontSize:11,color:'#5b6878',textAlign:'right'}}>{count}</div>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div style={{marginTop:28,display:'grid',gridTemplateColumns:'1fr 1fr',gap:18}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:700,color:'#15294a',marginBottom:10}}>Feedback Inbox ({feedbackRows.filter(f=>f.status!=='resolved').length} open)</div>
                    <div style={{display:'flex',flexDirection:'column',gap:8,maxHeight:420,overflowY:'auto'}}>
                      {feedbackRows.length===0 ? <div className="no-rows">No feedback yet.</div> : feedbackRows.map(f=>(
                        <div key={f.id} style={{background:'#fff',border:'1px solid #eef1f4',borderRadius:10,padding:'11px 13px',opacity:f.status==='resolved'?.55:1}}>
                          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:5}}>
                            <span style={{fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.04em',color:f.kind==='bug'?'#c0434a':f.kind==='idea'?'#137050':'#2457d6'}}>{f.kind}</span>
                            <span style={{fontSize:10.5,color:'#5b6878'}}>{f.page||'app'} · {new Date(f.created_at).toLocaleDateString(undefined,{month:'short',day:'numeric'})}</span>
                          </div>
                          <div style={{fontSize:12.5,color:'#26313c',whiteSpace:'pre-wrap',lineHeight:1.45}}>{f.message}</div>
                          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:7}}>
                            <span style={{fontSize:10.5,color:'#5b6878'}}>{f.actor_name||'—'}</span>
                            {f.status!=='resolved' && <button onClick={()=>resolveFeedback(f.id)} style={{fontSize:10.5,color:'#137050',background:'none',border:'none',cursor:'pointer',fontWeight:600}}>Mark resolved</button>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div style={{fontSize:13,fontWeight:700,color:'#15294a',marginBottom:10}}>AI Questions ({aiQuestions.length})</div>
                    <div style={{display:'flex',flexDirection:'column',gap:6,maxHeight:420,overflowY:'auto'}}>
                      {aiQuestions.length===0 ? <div className="no-rows">No AI questions yet.</div> : aiQuestions.map((q,i)=>(
                        <div key={i} style={{background:'#fff',border:'1px solid #eef1f4',borderRadius:10,padding:'9px 12px'}}>
                          <div style={{fontSize:12.5,color:'#26313c',lineHeight:1.4}}>{q.detail||(q.module==='chat'?'AI question (LSA chat) - text not stored':'AI question - text not stored')}</div>
                          <div style={{fontSize:10.5,color:'#5b6878',marginTop:4}}>{q.actor_name||'—'} · {new Date(q.created_at).toLocaleDateString(undefined,{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'})}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {tab==='companies' && (
          <div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
              <div style={{fontSize:16,fontWeight:700,color:'#15294a'}}>Management Companies</div>
              <button className="btn btn-primary" onClick={()=>setCompanyForm({name:'',email:'',password:''})}>+ New company</button>
            </div>
            {companyForm && (
              <div style={{background:'#fff',border:'1px solid #eef1f4',borderRadius:12,padding:14,marginBottom:16,display:'flex',gap:10,flexWrap:'wrap',alignItems:'center'}}>
                <input placeholder="Company name" value={companyForm.name} onChange={e=>setCompanyForm(f=>({...f,name:e.target.value}))} style={{padding:'7px 10px',border:'1px solid #dde3ea',borderRadius:7,fontSize:13}} />
                <input placeholder="Login email" value={companyForm.email} onChange={e=>setCompanyForm(f=>({...f,email:e.target.value}))} style={{padding:'7px 10px',border:'1px solid #dde3ea',borderRadius:7,fontSize:13}} />
                <input type="password" placeholder="Password" value={companyForm.password} onChange={e=>setCompanyForm(f=>({...f,password:e.target.value}))} style={{padding:'7px 10px',border:'1px solid #dde3ea',borderRadius:7,fontSize:13}} />
                <button className="btn btn-primary" onClick={createCompany}>Create</button>
                <button className="btn" onClick={()=>setCompanyForm(null)}>Cancel</button>
              </div>
            )}
            {companies.length===0 && <div style={{color:'#5b6878',fontSize:13,padding:20}}>No management companies yet.</div>}
            {companies.map(co => (
              <div key={co.id} style={{background:'#fff',border:'1px solid #eef1f4',borderRadius:12,padding:16,marginBottom:14}}>
                <div style={{fontWeight:700,fontSize:15,color:'#15294a'}}>{co.name}</div>
                <div style={{fontSize:12,color:'#5b6878',marginBottom:12}}>{co.email || 'no login email'} · {clients.filter(v=>v.company_id===co.id).length} vessels under management</div>
                <div style={{fontSize:11,color:'#5b6878',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:8}}>Vessels under management</div>
                <div style={{display:'flex',flexWrap:'wrap',gap:'8px 16px'}}>
                  {clients.map(v => (
                    <label key={v.id} style={{display:'flex',alignItems:'center',gap:6,fontSize:12.5,color:'#26313c',cursor:'pointer'}}>
                      <input type="checkbox" checked={v.company_id===co.id} onChange={e=>assignVessel(v.id, e.target.checked?co.id:null)} />
                      {v.vessel_name}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab==='settings' && <TimezoneCard clients={clients} config={config} onSaved={loadClients} />}

        {tab==='ai' && <AIActivity />}
        {tab==='partners' && <PartnerKeys />}
        {tab==='connectors' && <ConnectorsAdmin />}
        {tab==='email' && <div style={{padding:20,maxWidth:1100}}><EmailSettings /></div>}
        {tab==='brain' && <BrainMapAdmin />}

        {tab==='crew' && (
          <div>
            {clients.length === 0 ? <div className="no-rows">No vessels yet.</div> :
             clients.map(vessel => {
              const vesselCrew = crewList.filter(c => c.client_id === vessel.id)
              return (
                <div key={vessel.id} style={{marginBottom:22}}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
                    <h3 style={{fontSize:14,fontWeight:600,color:'var(--gold2)'}}>{vessel.vessel_name} <span style={{color:'#5b6878',fontWeight:400,fontSize:12}}>({vesselCrew.length} crew)</span></h3>
                    <button className="btn btn-primary" onClick={()=>{setCrewForm({role:'crew',client_id:vessel.id});setCrewError('');setCrewModal(true)}}>+ Add crew</button>
                  </div>
                  <div className="tbl-wrap">
                    <table>
                      <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Created</th><th>Actions</th></tr></thead>
                      <tbody>
                        {vesselCrew.length===0 ? <tr><td colSpan={6}><div className="no-rows">No crew accounts for this vessel.</div></td></tr>
                        : vesselCrew.map(c=>(
                          <tr key={c.id} className="data-row">
                            <td><strong style={{color:'var(--gold2)'}}>{c.name}</strong></td>
                            <td>{c.email}</td>
                            <td><span className="pill pill-b">{c.role}</span></td>
                            <td>{c.active ? <span className="pill pill-g">Active</span> : <span className="pill pill-r">Inactive</span>}</td>
                            <td>{c.created_at ? new Date(c.created_at).toLocaleDateString() : '-'}</td>
                            <td>
                            <div style={{display:'flex',gap:5}}>
                              <button className="act-btn" onClick={()=>{ setPermForm(c.permissions||{}); setPermCrew(c) }}>Permissions</button>
                              {c.active && <button className="act-btn del" onClick={()=>deactivateCrew(c.id)}>Deactivate</button>}
                            </div>
                          </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {permCrew && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.7)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }} onClick={()=>setPermCrew(null)}>
            <div style={{ background:'#fff', borderRadius:12, padding:24, width:620, maxWidth:'95vw', height:'80vh', overflowY:'auto' }} onClick={e=>e.stopPropagation()}>
              <div style={{ fontWeight:700, color:'#15294a', marginBottom:4 }}>Permissions — {permCrew.name}</div>
              <div style={{ fontSize:11, color:'#5b6878', marginBottom:16 }}>Set access level for each module. None hides it; Read views only; Write can edit; Full can delete.</div>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'center', margin:'0 0 8px' }}>
                <span style={{ fontSize:11, color:'#5b6878', width:52 }}>Set all</span>
                {['none','read','write','full'].map(lv => <button key={lv} className="act-btn" onClick={()=>setPermForm(Object.fromEntries(PERM_MODULES.map(([k])=>[k,lv])))}>{lv[0].toUpperCase()+lv.slice(1)}</button>)}
              </div>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'center', margin:'0 0 14px' }}>
                <span style={{ fontSize:11, color:'#5b6878', width:52 }}>Role</span>
                {Object.keys(PERM_PRESETS).map(r => <button key={r} className="act-btn" onClick={()=>setPermForm(applyPreset(r))}>{r}</button>)}
                <span style={{ fontSize:11, color:'#5b6878' }}>then adjust and Save</span>
              </div>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead style={{ position:'sticky', top:0, background:'#fff', zIndex:1 }}><tr>
                  <th style={{ textAlign:'left', fontSize:11, color:'#5b6878', paddingBottom:8 }}>Module</th>
                  {['None','Read','Write','Full'].map(l=><th key={l} style={{ textAlign:'center', fontSize:11, color:'#5b6878', paddingBottom:8, minWidth:50 }}>{l}</th>)}
                </tr></thead>
                <tbody>
                  {PERM_MODULES.map(([k,label])=>(
                    <tr key={k} style={{ borderBottom:'1px solid #eef1f4' }}>
                      <td style={{ color:'#26313c', fontSize:13, padding:'6px 0' }}>{label}</td>
                      {['none','read','write','full'].map(lv=>(
                        <td key={lv} style={{ textAlign:'center' }}>
                          <input type="radio" name={'perm_'+k} checked={(permForm[k]||'read')===lv} onChange={()=>setPermForm(pp=>({...pp,[k]:lv}))} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:16 }}>
                <button className="btn" onClick={()=>setPermCrew(null)}>Cancel</button>
                <button className="btn btn-primary" onClick={saveCrewPerms}>Save Permissions</button>
              </div>
            </div>
          </div>
        )}

        {tab==='vessels' && <div>
        <div style={{marginBottom:14,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <h3 style={{fontSize:13,fontWeight:500,color:'var(--text)'}}>Active clients ({clients.length})</h3>
          <button className="btn" onClick={loadClients} style={{fontSize:11}}>Refresh</button>
        </div>

        {loading && <div className="loading-row"><div className="spin" />Loading clients...</div>}

        <div className="tbl-wrap">
          <table>
            <thead><tr>
              <th>Vessel Name</th><th>Email</th><th>Plan</th><th>Ship's time</th><th>Created</th><th>Actions</th>
            </tr></thead>
            <tbody>
              {clients.length === 0 && !loading
                ? <tr><td colSpan={6}><div className="no-rows">No clients yet. Add your first client above.</div></td></tr>
                : clients.map(c => (
                  <tr key={c.id} className="data-row">
                    <td><strong style={{color:'var(--gold2)'}}>{c.vessel_name}</strong></td>
                    <td>{c.email}</td>
                    <td>
                      <span className="pill pill-b">{c.plan || 'starter'}</span>
                      {c.sub_status && c.sub_status!=='active' && <span className="pill" style={{marginLeft:5, background: c.sub_status==='suspended'||c.sub_status==='cancelled'?'#fbe6e6':'#fdf0d5', color: c.sub_status==='suspended'||c.sub_status==='cancelled'?'#8a1f1f':'#8a5a00'}}>{c.sub_status}</span>}
                    </td>
                    <td>{c.timezone ? <span style={{fontSize:12}}>{c.timezone}</span> : <span className="pill pill-r">not set</span>}</td>
                    <td>{c.created_at ? new Date(c.created_at).toLocaleDateString() : '-'}</td>
                    <td>
                      <div style={{display:'flex',gap:5}}>
                        <button className="act-btn" onClick={() => setManaging(c)}>Manage</button>
                        <button className="act-btn" onClick={() => setAiVessel(c)}>AI activity</button>
                        <button className="act-btn del" onClick={() => deleteClient(c)}>Remove</button>
                      </div>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
          {managing && <ManageVessel vessel={managing} onClose={()=>setManaging(null)} onSaved={loadClients} />}
          {aiVessel && (
            <div onClick={() => setAiVessel(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(8,16,30,.55)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '40px 16px' }}>
              <div onClick={e => e.stopPropagation()} style={{ background: 'var(--ink)', borderRadius: 14, width: 'min(1100px, 100%)', boxShadow: '0 20px 60px rgba(0,0,0,.35)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px 0' }}>
                  <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>AI activity &mdash; {aiVessel.vessel_name}</h3>
                  <button className="btn" onClick={() => setAiVessel(null)}>Close</button>
                </div>
                <AIActivity clientId={aiVessel.id} />
              </div>
            </div>
          )}
        </div>
      </div>}
      </div>

      {crewModal && (
        <Modal title="Add Crew Account" onClose={()=>setCrewModal(false)} onSave={handleCrewSave} saving={crewSaving} error={crewError}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0 16px'}}>
            {[
              {name:'name',label:'Full Name',placeholder:'John Smith',required:true},
              {name:'role',label:'Role',type:'select',options:['captain','chief_officer','engineer','steward','crew']},
              {name:'email',label:'Email',placeholder:'john@vessel.com',type:'email',required:true},
              {name:'password',label:'Temporary Password',placeholder:'Set a password',type:'password',required:true},
            ].map(f=>(
              <div key={f.name} className="field">
                <label>{f.label}{f.required?' *':''}</label>
                {f.type==='select'
                  ? <select value={crewForm[f.name]||'crew'} onChange={e=>setCrewForm(p=>({...p,[f.name]:e.target.value}))}>
                      {f.options.map(o=><option key={o} value={o}>{o.replace('_',' ')}</option>)}
                    </select>
                  : <input type={f.type||'text'} placeholder={f.placeholder} value={crewForm[f.name]||''} onChange={e=>setCrewForm(p=>({...p,[f.name]:e.target.value}))} />
                }
              </div>
            ))}
          </div>
        </Modal>
      )}

      {modal && (
        <Modal title="Add New Client" onClose={() => setModal(false)} onSave={handleSave} saving={saving} error={saveError}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0 16px'}}>
            {[
              {name:'vessel_name',label:'Vessel Name',placeholder:'M/Y Serenity',required:true},
              {name:'email',label:'Client Email',placeholder:'captain@vessel.com',type:'email',required:true},
              {name:'password',label:'Temporary Password',placeholder:'Set a password',type:'password',required:true},
              {name:'plan',label:'Plan',type:'select',options:['starter','professional','enterprise']},
            ].map(f => (
              <div key={f.name} className="field" style={{gridColumn: f.name==='vessel_name'||f.name==='email' ? 'span 1' : 'span 1'}}>
                <label>{f.label}{f.required?' *':''}</label>
                {f.type==='select'
                  ? <select value={form[f.name]||''} onChange={e=>setForm(p=>({...p,[f.name]:e.target.value}))}>
                      {f.options.map(o=><option key={o}>{o}</option>)}
                    </select>
                  : <input type={f.type||'text'} placeholder={f.placeholder} value={form[f.name]||''} onChange={e=>setForm(p=>({...p,[f.name]:e.target.value}))} />
                }
              </div>
            ))}
          </div>

        </Modal>
      )}
    </div>
  )
}

const styles = {
  loginPage: {
    minHeight: '100vh', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    background: 'var(--ink)',
  },
  loginBox: {
    background: 'var(--hull)', border: '0.5px solid var(--border2)',
    borderRadius: 14, padding: 32, width: 360,
  },
  page: {
    minHeight: '100vh', background: 'var(--ink)',
  },
  topBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 24px', background: 'var(--ink2)',
    borderBottom: '0.5px solid var(--border)',
  },
  content: {
    padding: 24, maxWidth: 1200, margin: '0 auto',
  },
}
